import { useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Share2, Download, X, Loader2, Check, Smartphone, Square } from 'lucide-react';
import { generateShareImage, shareImageFile, downloadImage } from '../../utils/share';
import { RankingTop5ShareCard, SHARE_CARD_SIZES, type ShareCardFormat } from './RankingTop5ShareCard';
import { trackEvent } from '../../utils/analytics';
import { canonicalOrigin } from '../../utils/canonicalOrigin';
import type { RankingPlayer, RankingLeaderboardMetric } from '../../types';

interface RankingShareButtonProps {
  rankingName: string;
  /** The last round that fed these standings, for the card's eyebrow. */
  roundNumber: number | null;
  imageUrl: string | null;
  leaderboard: RankingPlayer[];
  metric: RankingLeaderboardMetric;
}

type ShareState = 'choosing' | 'preparing' | 'ready' | 'sharing' | 'success' | 'error';

/** Hosts with broken CORS headers (e.g. duplicate Access-Control-Allow-Origin) */
const CORS_BLOCKLIST = ['ui-avatars.com'];

/** Preview width in the modal; the height follows the card's aspect ratio. */
const PREVIEW_WIDTH = 280;

const FORMAT_OPTIONS: { format: ShareCardFormat; label: string; hint: string; Icon: typeof Smartphone }[] = [
  { format: 'story', label: '9:16', hint: 'Stories', Icon: Smartphone },
  { format: 'feed', label: '4:5', hint: 'Feed', Icon: Square },
];

async function urlToDataUrl(url: string): Promise<string | null> {
  try {
    // no-store bypasses the browser HTTP cache which may hold a non-CORS response
    // from a prior <img> load (no Origin header → no CORS headers in cached response)
    const response = await fetch(url, { cache: 'no-store' });
    const blob = await response.blob();
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

function isCorsBlocked(url: string): boolean {
  return CORS_BLOCKLIST.some(host => url.includes(host));
}

export function RankingShareButton({
  rankingName,
  roundNumber,
  imageUrl,
  leaderboard,
  metric,
}: RankingShareButtonProps) {
  const [state, setState] = useState<ShareState>('choosing');
  const [showModal, setShowModal] = useState(false);
  const [format, setFormat] = useState<ShareCardFormat>('story');
  const [imageBlob, setImageBlob] = useState<Blob | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [captureImageUrl, setCaptureImageUrl] = useState<string | null>(null);
  const [capturePlayers, setCapturePlayers] = useState<RankingPlayer[]>([]);
  const cardRef = useRef<HTMLDivElement>(null);

  const cardSize = SHARE_CARD_SIZES[format];
  const previewHeight = Math.round((PREVIEW_WIDTH * cardSize.height) / cardSize.width);

  const inlineImages = useCallback(async () => {
    // Inline ranking image from R2
    const inlinedRankingImg = imageUrl && !isCorsBlocked(imageUrl)
      ? await urlToDataUrl(imageUrl)
      : null;
    setCaptureImageUrl(inlinedRankingImg);

    // Inline player avatars (skip broken CORS hosts)
    const top5 = leaderboard.slice(0, 5);
    const results = await Promise.all(
      top5.map(async (p) => {
        if (!p.avatarUrl || isCorsBlocked(p.avatarUrl)) return { ...p, avatarUrl: null };
        const dataUrl = await urlToDataUrl(p.avatarUrl);
        return { ...p, avatarUrl: dataUrl };
      })
    );
    setCapturePlayers(results);
  }, [imageUrl, leaderboard]);

  const generateImage = useCallback(async () => {
    await inlineImages();
    // Wait for re-render with the inlined data URLs and the chosen format
    await new Promise(resolve => setTimeout(resolve, 150));
    if (!cardRef.current) return;
    try {
      // The card is authored at its export size, so capture it 1:1
      const blob = await generateShareImage(cardRef.current, { scale: 1 });
      if (blob) {
        setImageBlob(blob);
        setPreviewUrl(prev => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(blob); });
        setState('ready');
      } else {
        setState('error');
      }
    } catch (error) {
      console.error('Error generating ranking image:', error);
      setState('error');
    }
  }, [inlineImages]);

  const handleOpenModal = () => {
    setShowModal(true);
    setState('choosing');
    trackEvent('btv_ranking_share_attempt');
  };

  const handleChooseFormat = async (chosen: ShareCardFormat) => {
    setFormat(chosen);
    setState('preparing');
    await generateImage();
  };

  const handleClose = () => {
    setShowModal(false);
    setState('choosing');
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setImageBlob(null);
  };

  const fileName = `ranking-${rankingName.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${format === 'story' ? '9x16' : '4x5'}`;

  const handleShare = async () => {
    if (!imageBlob) return;
    setState('sharing');

    const appUrl = canonicalOrigin();
    const shareText = `🏆 Top 5 do ranking "${rankingName}"!\n\nAcompanhe no BT Vision!\n\n${appUrl}`;
    const shared = await shareImageFile(imageBlob, `Ranking ${rankingName}`, shareText);

    if (shared) {
      trackEvent('btv_ranking_share_success', { method: 'web_share_api', format });
    } else {
      downloadImage(imageBlob, fileName);
      trackEvent('btv_ranking_share_download', { reason: 'web_share_fallback', format });
    }
    setState('success');
    setTimeout(handleClose, 1500);
  };

  const handleDownload = () => {
    if (!imageBlob) return;
    downloadImage(imageBlob, fileName);
    trackEvent('btv_ranking_share_download', { reason: 'direct_download', format });
    setState('success');
    setTimeout(handleClose, 1500);
  };

  const placeholderStyle = { width: PREVIEW_WIDTH, height: previewHeight };

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="w-full inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 px-6 py-3 text-base text-white/70 hover:text-white hover:bg-white/10"
      >
        <Share2 className="w-4 h-4" />
        Compartilhar Top 5
      </button>

      {showModal && createPortal(
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-deep-blue/95 backdrop-blur-sm"
            onClick={handleClose}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-sm"
            >
              <button
                onClick={handleClose}
                className="absolute -top-12 right-0 p-2 text-white/60 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>

              <div className="flex flex-col items-center">
                {state === 'choosing' && (
                  <div className="w-full rounded-3xl bg-white/5 border border-white/10 p-6">
                    <p className="text-white font-semibold text-center">Qual formato?</p>
                    <p className="text-white/50 text-sm text-center mt-1">
                      Escolha a proporção da imagem
                    </p>
                    <div className="grid grid-cols-2 gap-3 mt-5">
                      {FORMAT_OPTIONS.map(({ format: option, label, hint, Icon }) => (
                        <motion.button
                          key={option}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          onClick={() => handleChooseFormat(option)}
                          className="flex flex-col items-center gap-2 px-4 py-5 rounded-2xl bg-white/5 hover:bg-white/10 border border-white/10 hover:border-turquoise/50 transition-colors"
                        >
                          <Icon className="w-6 h-6 text-turquoise" />
                          <span className="text-white font-semibold text-base">{label}</span>
                          <span className="text-white/50 text-xs">{hint}</span>
                        </motion.button>
                      ))}
                    </div>
                  </div>
                )}

                {state === 'preparing' && (
                  <div
                    className="rounded-3xl flex items-center justify-center bg-white/5 border border-white/10"
                    style={placeholderStyle}
                  >
                    <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
                  </div>
                )}

                {state === 'ready' && previewUrl && (
                  <img
                    src={previewUrl}
                    alt="Preview"
                    className="rounded-3xl shadow-2xl"
                    style={{ width: PREVIEW_WIDTH }}
                  />
                )}

                {state === 'sharing' && (
                  <div
                    className="rounded-3xl flex items-center justify-center bg-white/5 border border-white/10"
                    style={placeholderStyle}
                  >
                    <Loader2 className="w-8 h-8 text-turquoise animate-spin" />
                  </div>
                )}

                {state === 'success' && (
                  <div
                    className="rounded-3xl flex flex-col items-center justify-center gap-4 bg-white/5 border border-white/10"
                    style={placeholderStyle}
                  >
                    <div className="w-16 h-16 rounded-full bg-success-green/20 flex items-center justify-center">
                      <Check className="w-8 h-8 text-success-green" />
                    </div>
                    <p className="text-white font-medium">Pronto!</p>
                  </div>
                )}

                {state === 'error' && (
                  <div
                    className="rounded-3xl flex flex-col items-center justify-center gap-4 p-6 bg-white/5 border border-white/10"
                    style={placeholderStyle}
                  >
                    <p className="text-coral font-medium">Erro ao gerar imagem</p>
                    <button
                      onClick={() => setState('choosing')}
                      className="px-4 py-2 bg-white/10 rounded-lg text-white/80"
                    >
                      Tentar novamente
                    </button>
                  </div>
                )}

                {/* Action buttons */}
                {state === 'ready' && (
                  <div className="flex gap-3 mt-4">
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleDownload}
                      className="flex items-center gap-1.5 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white text-sm transition-colors"
                    >
                      <Download className="w-4 h-4" />
                      <span>Baixar</span>
                    </motion.button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleShare}
                      className="flex items-center gap-1.5 px-4 py-2 bg-turquoise hover:bg-turquoise/90 rounded-lg text-deep-blue font-medium text-sm transition-colors"
                    >
                      <Share2 className="w-4 h-4" />
                      <span>Compartilhar</span>
                    </motion.button>
                  </div>
                )}
              </div>

              {/* Hidden card for capture — no external URLs to avoid CORS */}
              <div className="fixed -left-[9999px] top-0">
                <RankingTop5ShareCard
                  ref={cardRef}
                  rankingName={rankingName}
                  roundNumber={roundNumber}
                  imageUrl={captureImageUrl}
                  players={capturePlayers.length > 0 ? capturePlayers : leaderboard.slice(0, 5)}
                  metric={metric}
                  format={format}
                />
              </div>
            </motion.div>
          </motion.div>
        </AnimatePresence>,
        document.body
      )}
    </>
  );
}
